import { SizeMap } from '@/themes/custom/sizeENUM';
import {
  Components,
  Theme,
  CSSObject,
  alpha,
  ThemeOptions,
} from '@mui/material/styles';

// ─────────────────────────────────────────────────────────────
// TYPE DEFINITIONS
// ─────────────────────────────────────────────────────────────

type StepperVariantStyle = CSSObject;

const getStepperTheme = (theme: Theme): ThemeOptions => {
  // ─────────────────────────────────────────────────────────────
  // STYLE CONSTANTS
  // ─────────────────────────────────────────────────────────────

  // Stepper Size Map following standard pattern
  const stepperSize: SizeMap = {
    s: {
      width: '24px',
      height: '24px',
      fontSize: '12px',
      padding: theme.unit(0.5),
      borderRadius: '12px',
    },
    m: {
      width: '32px',
      height: '32px',
      fontSize: '14px',
      padding: theme.unit(1),
      borderRadius: '16px',
    },
    l: {
      width: '40px',
      height: '40px',
      fontSize: '16px',
      padding: theme.unit(1.5),
      borderRadius: '20px',
    },
    xl: {
      width: '48px',
      height: '48px',
      fontSize: '18px',
      padding: theme.unit(2),
      borderRadius: '24px',
    },
  };

  // Label size configurations
  const labelSizes: SizeMap = {
    s: { fontSize: '12px', fontWeight: 700, fontFamily: 'Archivo, sans-serif' },
    m: { fontSize: '14px', fontWeight: 700, fontFamily: 'Archivo, sans-serif' },
    l: { fontSize: '16px', fontWeight: 700, fontFamily: 'Archivo, sans-serif' },
    xl: {
      fontSize: '18px',
      fontWeight: 700,
      fontFamily: 'Archivo, sans-serif',
    },
  };

  // State color mappings
  const getStateColors = () => ({
    active:
      (theme.palette as any)?.M3?.sys?.light?.primary ||
      (theme.palette as any)?.primary?.main ||
      '#1976d2',
    completed:
      (theme.palette as any)?.M3?.sys?.light?.['primary-container'] ||
      (theme.palette as any)?.success?.main ||
      '#2e7d32',
    error:
      (theme.palette as any)?.State?.error ||
      (theme.palette as any)?.error?.main ||
      '#d32f2f',
    warning:
      (theme.palette as any)?.State?.warning ||
      (theme.palette as any)?.warning?.main ||
      '#ed6c02',
    pending: theme.palette.grey?.[300] || '#e0e0e0',
    skipped: theme.palette.grey?.[200] || '#f5f5f5',
    disabled: theme.palette.grey?.[100] || '#fafafa',
    // Label colors
    labelText: '#000000',
    labelDisabled: theme.palette?.text?.disabled || '#cccccc',
    // Connector colors
    connectorLine: '#4E5C79',
  });

  // ─────────────────────────────────────────────────────────────
  // COMPONENT STYLE OVERRIDES
  // ─────────────────────────────────────────────────────────────

  const stepperTheme: Components<Theme>['MuiStepper'] = {
    styleOverrides: {
      root: {
        padding: theme.unit(1, 0),
      },
      horizontal: {
        '& .MuiStep-root': {
          paddingLeft: theme.unit(1),
          paddingRight: theme.unit(1),
        },
      },
      vertical: {
        '& .MuiStep-root': {
          paddingBottom: theme.unit(2),
        },
      },
    },
  };

  const stepTheme: Components<Theme>['MuiStep'] = {
    styleOverrides: {
      root: {
        '& .MuiStepLabel-root': {
          cursor: 'default',
        },
        '&.clickable .MuiStepLabel-root': {
          cursor: 'pointer',
          '&:hover': {
            '& .MuiStepIcon-root': {
              transform: 'scale(1.05)',
              transition: 'transform 0.2s ease-in-out',
            },
          },
        },
      },
    },
  };

  const stepLabelTheme: Components<Theme>['MuiStepLabel'] = {
    styleOverrides: {
      label: {
        color: getStateColors().labelText,
        fontFamily: 'Archivo, sans-serif',
        fontWeight: 700,
        '&.Mui-active': {
          color: getStateColors().labelText,
          fontWeight: 700,
        },
        '&.Mui-completed': {
          color: getStateColors().labelText,
          fontWeight: 700,
        },
        '&.Mui-disabled': {
          color: getStateColors().labelDisabled,
        },
      },
      labelContainer: {
        '& .MuiTypography-caption': {
          marginTop: theme.unit(0.5),
        },
      },
    },
  };

  const stepIconTheme: Components<Theme>['MuiStepIcon'] = {
    styleOverrides: {
      root: {
        '&.Mui-active': {
          color: getStateColors().active,
        },
        '&.Mui-completed': {
          color: getStateColors().completed,
        },
        '&.Mui-error': {
          color: getStateColors().error,
        },
      },
      text: {
        fontWeight: 600,
      },
    },
  };

  const stepConnectorTheme: Components<Theme>['MuiStepConnector'] = {
    styleOverrides: {
      root: {
        '&.Mui-active .MuiStepConnector-line': {
          borderColor: getStateColors().connectorLine,
        },
        '&.Mui-completed .MuiStepConnector-line': {
          borderColor: getStateColors().connectorLine,
        },
      },
      line: {
        borderColor: getStateColors().connectorLine,
        borderTopWidth: 2,
      },
      alternativeLabel: {
        top: 22,
        left: 'calc(-50% + 16px)',
        right: 'calc(50% + 16px)',
      },
      vertical: {
        marginLeft: 20,
        minHeight: 32,
        '& .MuiStepConnector-line': {
          borderLeftWidth: 2,
          borderTopWidth: 0,
        },
      },
    },
  };

  // ─────────────────────────────────────────────────────────────
  // THEME EXPORT
  // ─────────────────────────────────────────────────────────────

  return {
    components: {
      MuiStepper: stepperTheme,
      MuiStep: stepTheme,
      MuiStepLabel: stepLabelTheme,
      MuiStepIcon: stepIconTheme,
      MuiStepConnector: stepConnectorTheme,
    },
    size: { stepper: stepperSize, stepperLabel: labelSizes },
  };
};

export default getStepperTheme;

/**
 * Legacy export for backward compatibility
 */
export const stepperTheme = getStepperTheme;
export const getStepperComponentOverrides = (theme: Theme) => {
  const config = getStepperTheme(theme);
  return config.components;
};
